import { useMemo, useState } from 'react'
import { disablePro, enablePro } from '../../api/pros'
import { useMe } from '../../hooks/useMe'
import { getToken } from '../../lib/auth'

type StatusSummary = {
  enabled: boolean
  visibleInDiscovery: boolean
  bookingEnabled: boolean
  lastUpdatedAt?: string | null
}

export default function ProsSettingsTab({ enabled, status, onChanged }: { enabled: boolean; status: StatusSummary; onChanged: () => void }) {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)
  const showAdvanced = Boolean(me?.is_staff || import.meta.env.DEV || import.meta.env.VITE_SHOW_DEV_SWITCH === 'true')
  const [saving, setSaving] = useState(false)
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [disableAcknowledge, setDisableAcknowledge] = useState(false)
  const [devTools, setDevTools] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [riskFlag, setRiskFlag] = useState(false)

  const statusRows = useMemo(
    () => [
      { label: 'Pro enabled', value: status.enabled ? 'Yes' : 'No' },
      { label: 'Visible in discovery', value: status.visibleInDiscovery ? 'Yes' : 'No' },
      { label: 'Booking enabled', value: status.bookingEnabled ? 'Yes' : 'No' },
      { label: 'Last updated', value: status.lastUpdatedAt ? new Date(status.lastUpdatedAt).toLocaleString() : '—' },
    ],
    [status.bookingEnabled, status.enabled, status.lastUpdatedAt, status.visibleInDiscovery],
  )

  const onToggleEnabled = async () => {
    setSaving(true)
    setError(null)
    try {
      if (enabled) await disablePro()
      else await enablePro()
      onChanged()
      setShowDisableModal(false)
      setDisableAcknowledge(false)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to update Pro status.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-white/10 bg-white/5 p-4">
        <div className="text-sm font-semibold text-white">Status</div>
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
          {statusRows.map((row) => (
            <div key={row.label} className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-white/55">{row.label}</div>
              <div className="mt-1 text-sm text-white/90">{row.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 p-4">
        <div className="text-sm font-semibold text-rose-100">Danger zone</div>
        <div className="mt-2 text-sm text-rose-100/80">
          {enabled ? 'Disable Pro to pause discovery and new request intake. Existing jobs remain accessible.' : 'Pro is currently disabled.'}
        </div>
        <button
          type="button"
          onClick={() => (enabled ? setShowDisableModal(true) : void onToggleEnabled())}
          disabled={saving}
          className={`mt-3 rounded-lg px-3 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
            enabled ? 'bg-rose-600 hover:bg-rose-700' : 'bg-white/15 hover:bg-white/20'
          }`}
        >
          {saving ? 'Saving...' : enabled ? 'Disable Pro' : 'Enable Pro'}
        </button>
      </div>

      {showAdvanced ? (
        <div className="rounded-xl border border-white/10 bg-white/5 p-4">
          <div className="text-sm font-semibold text-white">Advanced</div>
          <label className="mt-3 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={devTools} onChange={(e) => setDevTools(e.target.checked)} />
            Dev tools
          </label>
          <label className="mt-3 inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={riskFlag} onChange={(e) => setRiskFlag(e.target.checked)} />
            Risk flag (staff-only preview)
          </label>
        </div>
      ) : null}

      {error ? <div className="text-sm text-red-300">{error}</div> : null}

      {showDisableModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-xl border border-white/10 bg-neutral-950 p-4">
            <div className="text-base font-semibold text-white">Disable Pro?</div>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-white/80">
              <li>Hide your Pro card from discovery</li>
              <li>Stop receiving new requests</li>
              <li>Existing jobs remain accessible</li>
              <li>You can re-enable later</li>
            </ul>
            <label className="mt-3 inline-flex items-center gap-2 text-sm text-white/85">
              <input type="checkbox" checked={disableAcknowledge} onChange={(e) => setDisableAcknowledge(e.target.checked)} />
              I understand
            </label>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDisableModal(false)
                  setDisableAcknowledge(false)
                }}
                className="rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white hover:bg-white/10"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void onToggleEnabled()}
                disabled={!disableAcknowledge || saving}
                className="rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {saving ? 'Disabling...' : 'Disable Pro'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
